import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { SHIFT_TYPE_LABELS, fmtShiftType, fmtRequestDate, fmtTimeRange, toTimeInput, todayPacific } from '../utils/shiftRequests';

const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const blankLine = (prev) => ({
  key: newKey(),
  // Property carries forward from the line above; date starts empty.
  clientid: prev?.clientid || '',
  shdate: '',
  shiftType: '',
  excludeTypes: [],
  tmstart: '',
  tmend: ''
});

// "16:30" -> 1630; '' -> null (same shape the server stores)
const fromTimeInput = (v) => (v ? parseInt(v.replace(':', ''), 10) : null);

// Unsent lines survive a tab switch or refresh. Keyed per client; localStorage
// is wiped on sign-out, which discards them along with the session.
const linesKey = () => `requestLines:${localStorage.getItem('clientName') || ''}`;
function readLines() {
  try {
    const saved = JSON.parse(localStorage.getItem(linesKey()));
    return Array.isArray(saved) && saved.length ? saved : null;
  } catch { return null; }
}
function writeLines(lines) {
  try { localStorage.setItem(linesKey(), JSON.stringify(lines)); } catch { /* storage unavailable */ }
}

// A trailing line that was added but never filled in is ignored on submit.
const isUntouched = (l) => !l.shiftType && !l.tmstart && !l.tmend;
const linesToSend = (lines) =>
  lines.filter((l, i) => !(lines.length > 1 && i === lines.length - 1 && isUntouched(l)));

// An input plus (for date/time) the field name shown inside it while empty (phone layout only —
// see .req-ph in index.css; desktop has a single header row instead).
function Field({ area, label, value, children }) {
  return (
    <div className={`req-field ${area}`}>
      {children}
      {label && !value && <span className="req-ph">{label}</span>}
    </div>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const hourLabel = h => `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
const pad2 = n => String(n).padStart(2, '0');

// Optional time in 5-minute steps, as an hour + minute dropdown pair. The
// browser's own time picker ignores a 5-minute step on most phones. Value is
// "HH:MM" or '' (same shape <input type="time"> used to produce).
function TimeSelect({ value, onChange, label }) {
  const [h, m] = value ? value.split(':').map(Number) : [null, null];
  return (
    <div className="req-time">
      <select
        className={value ? '' : 'empty'}
        value={value ? h : ''}
        onChange={e => onChange(e.target.value === '' ? '' : `${pad2(e.target.value)}:${pad2(m ?? 0)}`)}
        aria-label={`${label} hour`}
      >
        <option value="">{label}</option>
        {HOURS.map(x => <option key={x} value={x}>{hourLabel(x)}</option>)}
      </select>
      <select
        className={value ? '' : 'empty'}
        value={value ? m : ''}
        disabled={!value}
        onChange={e => onChange(`${pad2(h)}:${pad2(e.target.value)}`)}
        aria-label={`${label} minute`}
      >
        {!value && <option value="">:--</option>}
        {MINUTES.map(x => <option key={x} value={x}>:{pad2(x)}</option>)}
      </select>
    </div>
  );
}

// Client asks us to book shifts. The form is a set of line items — "+ Add Shift"
// adds a line, Submit sends them all at once. Editing a submitted request loads
// it as the single line and saves in place. Submitted requests are listed below,
// grouped by property, and removed automatically 7 days after their date.
export default function PortalRequests() {
  const [options, setOptions] = useState({ clientids: [], shiftTypes: [] });
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lines, setLines] = useState(() => readLines() || [blankLine()]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [badKey, setBadKey] = useState(null);
  const topRef = useRef(null);

  // Only persist new-request lines, never an in-progress edit of a saved request.
  useEffect(() => { if (!editingId) writeLines(lines); }, [lines, editingId]);

  // On a phone the Submit button is far below the error banner — bring it into view.
  useEffect(() => {
    if (error && topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [error]);

  useEffect(() => {
    api.getPortalRequestOptions().then(o => {
      setOptions(o);
      // One property? Pre-select it so each line is one less step.
      if (o.clientids.length === 1) {
        setLines(ls => ls.map(l => (l.clientid ? l : { ...l, clientid: o.clientids[0] })));
      }
    }).catch(err => setError(err.message));
    load();
  }, []);

  function load() {
    return api.getPortalRequests()
      .then(rows => { setRequests(rows); setLoaded(true); })
      .catch(err => { setError(err.message); setLoaded(true); });
  }

  const specificTypes = options.shiftTypes.filter(t => t !== 'ANY');
  const defaultProperty = options.clientids.length === 1 ? options.clientids[0] : '';

  const updateLine = (key, patch) => {
    if (key === badKey) setBadKey(null);
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));
  };
  const toggleExclude = (line, t) => updateLine(line.key, {
    excludeTypes: line.excludeTypes.includes(t) ? line.excludeTypes.filter(x => x !== t) : [...line.excludeTypes, t]
  });
  const addLine = () => setLines(ls => [...ls, blankLine(ls[ls.length - 1])]);
  // Picking a property also fills any lines below that don't have one yet.
  const setProperty = (key, clientid) => {
    if (key === badKey) setBadKey(null);
    setLines(ls => {
      const at = ls.findIndex(l => l.key === key);
      return ls.map((l, i) => (i === at || (i > at && !l.clientid) ? { ...l, clientid } : l));
    });
  };
  const removeLine = (key) => setLines(ls =>
    ls.length > 1 ? ls.filter(l => l.key !== key) : [blankLine({ clientid: defaultProperty })]);

  function startEdit(r) {
    setError('');
    setBadKey(null);
    setEditingId(r._id);
    setLines([{
      key: newKey(),
      clientid: r.clientid,
      shdate: r.shdate,
      shiftType: r.shiftType,
      excludeTypes: r.excludeTypes || [],
      tmstart: toTimeInput(r.tmstart),
      tmend: toTimeInput(r.tmend)
    }]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelEdit() {
    setEditingId(null);
    setBadKey(null);
    setLines(readLines() || [blankLine({ clientid: defaultProperty })]);
  }

  const toPayload = (l) => ({
    clientid: l.clientid,
    shdate: l.shdate,
    shiftType: l.shiftType,
    excludeTypes: l.shiftType === 'ANY' ? specificTypes.filter(t => l.excludeTypes.includes(t)) : [],
    tmstart: fromTimeInput(l.tmstart),
    tmend: fromTimeInput(l.tmend)
  });

  const toSend = editingId ? lines : linesToSend(lines);

  function fail(message, line) {
    setError(message);
    setBadKey(line ? line.key : null);
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBadKey(null);
    for (let i = 0; i < toSend.length; i++) {
      const l = toSend[i];
      const label = toSend.length > 1 ? `Shift ${i + 1}: ` : '';
      const missing = [!l.clientid && 'property', !l.shdate && 'date', !l.shiftType && 'shift type'].filter(Boolean);
      if (missing.length) return fail(`${label}Choose a ${missing.join(', ')}.`, l);
      if (l.shiftType === 'ANY' && l.excludeTypes.length >= specificTypes.length) {
        return fail(`${label}Leave at least one shift type un-excluded.`, l);
      }
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.updatePortalRequest(editingId, toPayload(lines[0]));
        cancelEdit();
      } else {
        await api.createPortalRequests(toSend.map(toPayload));
        setLines([blankLine({ clientid: defaultProperty })]);
      }
      await load();
    } catch (err) {
      fail(err.message, editingId ? lines[0] : toSend[err.data?.index]);
    } finally {
      setSaving(false);
    }
  }

  async function remove(r) {
    if (!window.confirm(`Delete ${fmtShiftType(r)} on ${fmtRequestDate(r.shdate)} at ${r.clientid}?`)) return;
    setError('');
    try {
      await api.deletePortalRequest(r._id);
      if (editingId === r._id) cancelEdit();
      await load();
    } catch (err) { setError(err.message); }
  }

  const grouped = useMemo(() => {
    const map = {};
    requests.forEach(r => { (map[r.clientid] = map[r.clientid] || []).push(r); });
    return Object.keys(map).sort((a, b) => a.localeCompare(b)).map(p => [p, map[p]]);
  }, [requests]);

  return (
    <div ref={topRef} style={{ scrollMarginTop: 12 }}>
      {error && <div className="alert alert-error">{error}</div>}

      <form className="card" onSubmit={submit}>
        <div className="card-title">{editingId ? 'Edit Request' : 'Request Shifts'}</div>
        {options.clientids.length === 0 && loaded ? (
          <p style={{ color: '#718096', fontSize: 13 }}>
            No properties are set up on your account yet. Contact us to add one.
          </p>
        ) : (
          <>
            <div className="req-head">
              <span />
              <span>Property</span>
              <span>Date</span>
              <span>Shift Type</span>
              <span>Start <span className="opt">(optional)</span></span>
              <span>End <span className="opt">(optional)</span></span>
              <span />
            </div>

            {lines.map((l, i) => (
              <div key={l.key} className={`req-line${l.key === badKey ? ' bad' : ''}`}>
                <div className="req-card-head">
                  <span>{editingId ? 'Shift' : `Shift ${i + 1}`}</span>
                  {!editingId && lines.length > 1 && (
                    <button type="button" className="req-card-remove" onClick={() => removeLine(l.key)}>Remove</button>
                  )}
                </div>

                <div className="req-main">
                  <span className="req-num">{lines.length > 1 ? i + 1 : ''}</span>
                  <Field area="req-prop">
                    <select
                      className={l.clientid ? '' : 'empty'}
                      value={l.clientid}
                      onChange={e => setProperty(l.key, e.target.value)}
                      aria-label="Property"
                    >
                      <option value="">Choose property</option>
                      {options.clientids.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field area="req-date" label="Date" value={l.shdate}>
                    <input
                      type="date"
                      className={l.shdate ? '' : 'empty'}
                      value={l.shdate}
                      min={editingId ? undefined : todayPacific()}
                      onChange={e => updateLine(l.key, { shdate: e.target.value })}
                      aria-label="Date"
                    />
                  </Field>
                  <Field area="req-type">
                    <select
                      className={l.shiftType ? '' : 'empty'}
                      value={l.shiftType}
                      onChange={e => updateLine(l.key, { shiftType: e.target.value, excludeTypes: [] })}
                      aria-label="Shift type"
                    >
                      <option value="">Choose shift</option>
                      {options.shiftTypes.map(t => <option key={t} value={t}>{SHIFT_TYPE_LABELS[t] || t}</option>)}
                    </select>
                  </Field>
                  <Field area="req-start">
                    <TimeSelect label="Start" value={l.tmstart} onChange={v => updateLine(l.key, { tmstart: v })} />
                  </Field>
                  <Field area="req-end">
                    <TimeSelect label="End" value={l.tmend} onChange={v => updateLine(l.key, { tmend: v })} />
                  </Field>
                  {!editingId ? (
                    <button
                      type="button"
                      className="btn btn-secondary req-del"
                      title="Remove this shift"
                      aria-label={`Remove shift ${i + 1}`}
                      onClick={() => removeLine(l.key)}
                    >✕</button>
                  ) : <span className="req-del" />}
                </div>

                {l.shiftType === 'ANY' && (
                  <div className="req-except">
                    <span className="req-except-label">Except (tap to exclude):</span>
                    {specificTypes.map(t => (
                      <button
                        key={t}
                        type="button"
                        className={`req-chip${l.excludeTypes.includes(t) ? ' on' : ''}`}
                        aria-pressed={l.excludeTypes.includes(t)}
                        onClick={() => toggleExclude(l, t)}
                      >
                        {SHIFT_TYPE_LABELS[t] || t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="req-actions">
              {!editingId && (
                <button type="button" className="btn btn-secondary" onClick={addLine}>+ Add Shift</button>
              )}
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {editingId
                  ? (saving ? 'Saving…' : 'Save Changes')
                  : (saving ? 'Submitting…' : toSend.length > 1 ? `Submit ${toSend.length} Requests` : 'Submit Request')}
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
              )}
            </div>
          </>
        )}
      </form>

      {loaded && requests.length === 0 && (
        <div className="card" style={{ color: '#718096' }}>No submitted requests yet.</div>
      )}

      {grouped.map(([property, rows]) => (
        <div key={property} className="card" style={{ marginBottom: 10 }}>
          <div className="card-title req-group-title">
            <span>{property}</span>
            <span className="req-group-count">{rows.length} request{rows.length === 1 ? '' : 's'}</span>
          </div>
          {rows.map(r => (
            <div key={r._id} className={`req-item${editingId === r._id ? ' editing' : ''}`}>
              <div className="req-item-main">
                <div className="req-item-title">{fmtRequestDate(r.shdate)} · {fmtShiftType(r)}</div>
                <div className="req-item-sub">
                  <span>{fmtTimeRange(r)}</span>
                  <span className={`badge ${r.running ? 'badge-paid' : 'badge-warn'}`}>
                    {r.running ? 'Running' : 'Requested'}
                  </span>
                </div>
              </div>
              <div className="req-item-btns">
                <button className="btn btn-secondary req-item-btn" onClick={() => startEdit(r)}>Edit</button>
                <button className="btn btn-danger req-item-btn" onClick={() => remove(r)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
